import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ApplicationService } from '@app/applications/application.service';
import { LoanApplication, ApplicationAccessSpecifier } from '@app/applications/applications.model';
import { ValidationService } from '../../../application-validators/validation.service'
import { MatSnackBar } from '@angular/material';
import { ReferenceCodeService } from '@app/admin/reference-code/reference-code.service';
import { NameValueDto } from '@app/loan-od-accounts/name-value-dto';
import { DateFormatPipe } from '@app/utils/date-format.pipe';
@Component({
  selector: 'eng-character-assessment',
  templateUrl: 'character-assessment.template.html',
  styleUrls: ['../../application-details.styles.scss', './character-assessment.scss']
})
export class CharacterAssessmentComponent implements OnInit {

  @Input()
  application: LoanApplication;
  @Input()
  menuCode: string;

  characterAssessmentCompositeDto: any = {};
  characterAssessmentForm: FormGroup;
  isFormDisabled: boolean;

  allowAccess: boolean;
  menuItemAllowAccess: boolean;
  applicationAccessSpecifiers: ApplicationAccessSpecifier[];
  menuItemAccessSpecifier: ApplicationAccessSpecifier;
  characters: NameValueDto[];
  bureauResponse: any;
  // maxDate variable is used for not showing future date
  maxDate: Date;
  constructor(private formBuilder: FormBuilder,
    private applicationService: ApplicationService,
    private _snackbar: MatSnackBar,
    private validationService: ValidationService,
    private referenceCodeService: ReferenceCodeService,
    private customDatepipe: DateFormatPipe) {
    this.characterAssessmentForm = this.formBuilder.group({
      character: [''],
      residenceNeighbourFeedback: [''],
      businessNeighbourFeedback: [''],
      remarks: [''],
      referenceFeedback: [''],
      riskIndicators: [''],
      numberOfBounces: [''],
      numberOfActiveLoans: [''],
      numberOfTranches: [''],
      conditions: [''],
      recommendationReason: [''],
      supervisorVisitDate: null,
      insuranceHolderName: [''],
      supervisorVisitComments: [''],
      supervisorRemarks: ['']
    });
    this.allowAccess = this.applicationService.allowAccess;
    this.maxDate = new Date();
  }

  ngOnInit() {
    this.isFormDisabled = true;
    this.applyValidatorsToCharacterDetails();
    this.getRefCodes();
    this.fetchCharacterAssessment();
    this.getBureauDetails();
    this.getMenuItemAccess();
  }

  applyValidatorsToCharacterDetails() {
    this.validationService.applyValidationRules(this.characterAssessmentForm, "CharacterAssessmentDetails").then((controlValidators) => {
    }).catch(() => {
    })
  }

  getRefCodes() {
    this.referenceCodeService.getShortRefCodes('character').subscribe((response: any) => {
      this.characters = response.character;
    });
  }

  fetchCharacterAssessment() {
    this.applicationService.getCharacterAssessment(this.application.uid).subscribe((response: any) => {
      if (response.body && response.body.characterAssessment) {
        this.characterAssessmentCompositeDto = response.body;
        this.buildFinancialInfoForm(response.body.characterAssessment);
      }
    });
  }

  buildFinancialInfoForm(characterAssessment?: any) {
    const characterAssessmentData = characterAssessment.characterAssessmentData || {};

    if (characterAssessmentData) {
      this.characterAssessmentForm.patchValue({
        character: characterAssessmentData.character,
        residenceNeighbourFeedback: characterAssessmentData.residenceNeighbourFeedback,
        businessNeighbourFeedback: characterAssessmentData.businessNeighbourFeedback,
        remarks: characterAssessmentData.remarks,
        referenceFeedback: characterAssessmentData.referenceFeedback,
        riskIndicators: characterAssessmentData.riskIndicators,
        numberOfBounces: characterAssessmentData.numberOfBounces,
        numberOfActiveLoans: characterAssessmentData.numberOfActiveLoans,
        numberOfTranches: characterAssessmentData.numberOfTranches,
        conditions: characterAssessmentData.conditions,
        recommendationReason: characterAssessmentData.recommendationReason,
        supervisorVisitDate: characterAssessmentData.supervisorVisitDate ? new Date(characterAssessmentData.supervisorVisitDate) : null,
        insuranceHolderName: characterAssessmentData.insuranceHolderName,
        supervisorVisitComments: characterAssessmentData.supervisorVisitComments,
        supervisorRemarks: characterAssessmentData.supervisorRemarks
      });
    }
    this.characterAssessmentForm.disable();
  }

  getBureauDetails() {
    this.applicationService.getBureauScore(this.application.uid).subscribe((response: any) => {
      this.bureauResponse = response.body;
      this.bureauResponse.forEach(applicant => {
        applicant.bureauInformation.sort((a, b) =>
          a.bureauReportName.localeCompare(b.bureauReportName)
        );
      });
    });
  }

  saveCharacterDetails() {
    this.validationService.markFormGroupTouched(this.characterAssessmentForm)
    if (!this.isFormDisabled && this.characterAssessmentForm.valid) {

      const formValue = this.characterAssessmentForm.getRawValue();

      if (!this.characterAssessmentCompositeDto) {
        this.characterAssessmentCompositeDto = {};
      }

      if (!this.characterAssessmentCompositeDto.characterAssessment) {
        this.characterAssessmentCompositeDto.characterAssessment = {};
      }

      const characterAssessmentData = this.characterAssessmentCompositeDto.characterAssessment;

      if (!characterAssessmentData.uid) {
        characterAssessmentData.category = 'CHARACTER';
        characterAssessmentData.contextUid = this.application.uid;
        characterAssessmentData.contextType = 'application';
      }

      characterAssessmentData.characterAssessmentData = {
        character: formValue.character,
        residenceNeighbourFeedback: formValue.residenceNeighbourFeedback,
        businessNeighbourFeedback: formValue.businessNeighbourFeedback,
        remarks: formValue.remarks,
        referenceFeedback: formValue.referenceFeedback,
        riskIndicators: formValue.riskIndicators,
        numberOfBounces: formValue.numberOfBounces,
        numberOfActiveLoans: formValue.numberOfActiveLoans,
        numberOfTranches: formValue.numberOfTranches,
        conditions: formValue.conditions,
        recommendationReason: formValue.recommendationReason,
        supervisorVisitDate: this.customDatepipe.transform(formValue.supervisorVisitDate, 'ISODATE'),
        insuranceHolderName: formValue.insuranceHolderName,
        supervisorVisitComments: formValue.supervisorVisitComments,
        supervisorRemarks: formValue.supervisorRemarks
      };

      const payload = {
        characterAssessment: characterAssessmentData
      };

      this.applicationService.saveCharacterAssessment(this.application.uid, payload).toPromise().then(
        (_success) => {
          this._snackbar.open("Character updated successfully", "Close", {
            duration: 2000,
          });
          this.isFormDisabled = true;
          this.fetchCharacterAssessment();
          this.characterAssessmentForm.disable();
        }, (failure) => {
          let errormesg = []
          errormesg.push(failure)
          this.applicationService.displayErrorMessages(errormesg);
        }
      );
    }
  }

  enableDisableForm() {
    this.characterAssessmentForm.enable();
    this.isFormDisabled = false;
  }

  cancelForm() {
    this.isFormDisabled = true;
    this.characterAssessmentForm.disable();
  }

  //this function will get either this menu item  is editable or readonly 
  getMenuItemAccess() {
    this.applicationService.getApplicationAccessModifiers(this.application.uid)
      .subscribe((response) => {
        this.applicationAccessSpecifiers = response.body;
        this.menuItemAccessSpecifier = this.applicationAccessSpecifiers
          .find(accessSpecifier => accessSpecifier.category === this.menuCode);
        if (this.menuItemAccessSpecifier) {
          this.menuItemAllowAccess = this.menuItemAccessSpecifier.allowAccess;
        }
      });
  }
}